import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { PageHeader, Section, StatTile } from '../components/Scaffold';
import CafeInsightsModal from '../components/CafeInsightsModal';
import api from '../api/client';

/**
 * Billing & Revenue — platform revenue overview.
 *
 * Wired to the live SuperAdmin endpoints:
 *   - GET /internal/dashboard       totals (MRR = monthly_revenue, expiring_soon)
 *   - GET /internal/cafes           per-café revenue (MTD) + max_pcs
 *   - GET /internal/revenue-series  platform revenue trend (3/6/12mo or a custom range)
 *
 * Revenue = each café's own sales (wallet top-ups + completed orders), the same
 * figure the café's Financials page shows.
 */

const formatCompact = (v) => {
  v = v || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
const formatCurrency = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
// Platform-fee figures are paise-level (a 0.5% cash fee on a small sale can be
// a few paise) — rounding to whole rupees like formatCurrency does above
// hides every change, so the fee table needs its own 2-decimal formatter.
// A rate fraction as a percentage ("0.025" -> "2.5%").
const fmtRatePct = (f) => `${Math.round(Number(f) * 10000) / 100}%`;

// What a café with NO rate of its own pays, in words. One number when the three
// rails agree, otherwise each named — a single figure there would be wrong for
// one of them.
const normalRatesLabel = (n) => {
  if (!n) return '';
  const { razorpay: a, payu: b, cash: c } = n;
  if (a === b && b === c) return fmtRatePct(a);
  return `Razorpay ${fmtRatePct(a)}, PayU ${fmtRatePct(b)}, cash ${fmtRatePct(c)}`;
};

// The café's fee state as a badge, in the SAME colours the café list and café
// detail pages use: Free trial grey, Paying green, Trial amber — outlined when
// it ends within a week so it is not missed.
const TRIAL_SOON_DAYS = 7;
const feeStateBadge = (row, startsOn) => {
  if (!startsOn) return { label: 'Free trial', tone: 'neutral' };
  if (row.fee_active) return { label: `Paying since ${startsOn}`, tone: 'success' };
  const days = Math.ceil((new Date(row.fee_starts_at).getTime() - Date.now()) / 86400000);
  if (days >= 0 && days <= TRIAL_SOON_DAYS) {
    const when = days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`;
    return { label: `Trial ends ${when} · ${startsOn}`, tone: 'warning', soon: true };
  }
  return { label: `Trial until ${startsOn}`, tone: 'warning' };
};

// The same, short enough for a pill: "2.5%", or "2–2.5%" when the rails
// differ. The full breakdown goes in the pill's tooltip.
const normalRatesShort = (n) => {
  if (!n) return '';
  const vals = [n.razorpay, n.payu, n.cash].map(Number);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  return lo === hi ? fmtRatePct(lo) : `${fmtRatePct(lo).slice(0, -1)}–${fmtRatePct(hi)}`;
};

// Shared look for the clickable pills in the fee columns: the badge's own size,
// on one line, with nothing a <button> adds by default.
const PILL_BUTTON_STYLE = {
  cursor: 'pointer',
  border: 'none',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const formatFeeCurrency = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

// Every cafe on this platform trades in IST (the backend hard-codes CAFE_TZ to
// Asia/Kolkata), so a fee start date entered here means IST midnight — the top
// of a trading day. Sending the browser's local midnight instead would start
// the fee partway through the cafe's day for anyone operating from another
// timezone, which is a real difference in what a customer is charged.
const IST_OFFSET = '+05:30';
const dateToIstMidnightIso = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T00:00:00${IST_OFFSET}`).toISOString() : null;
// The inverse, for populating the date input: render the stored instant back in
// IST so a date saved as the 1st does not display as the 31st to a browser west
// of India.
const isoToIstDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
};
const formatIstDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  }).format(d);
};

// A café is genuinely "on trial" only when it has a fee_starts_at date AND
// that date hasn't arrived yet (fee_active false) — matching the "Trial
// until X" badge in the table below. No date at all is the OTHER free
// state (never dated), and fee_active true means the trial is over.
const isOnTrial = (row) => !!row.fee_starts_at && !row.fee_active;

// Days left before `fee_starts_at`, for ordering the on-trial rows only —
// smallest (soonest to be billed) first.
const daysLeftInTrial = (row) => {
  const ms = new Date(row.fee_starts_at).getTime() - Date.now();
  return Number.isNaN(ms) ? Infinity : ms / 86400000;
};

// Trial cafés float to the top, soonest-ending first; everything else (never
// dated, or already paying) keeps whatever order it arrived in.
const sortTrialsFirst = (rows) => {
  const onTrial = rows.filter(isOnTrial).sort((a, b) => daysLeftInTrial(a) - daysLeftInTrial(b));
  const rest = rows.filter((r) => !isOnTrial(r));
  return [...onTrial, ...rest];
};

const REVENUE_RANGE_MONTHS = { '3m': 3, '6m': 6, '12m': 12 };
// Upper bound for both date pickers — there's no revenue from the future.
const todayIso = new Date().toISOString().slice(0, 10);

export default function Billing() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [cafes, setCafes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insightsCafe, setInsightsCafe] = useState(null);

  // Page-wide revenue range: ONE selector drives the Revenue stat tile, the
  // Revenue trend chart, and the Revenue by café table together, so picking
  // "Custom range" shows that same window everywhere on the page. "This
  // month" is the default and matches the original always-MTD behavior
  // (no extra fetch — reuses dash.monthly_revenue / cafes[].revenue as-is).
  // Total cafés / Active licenses / Expiring soon are current-state counts,
  // not revenue-over-time, so they intentionally stay unaffected by this.
  const [billingRange, setBillingRange] = useState('this_month');
  const [customRangeStart, setCustomRangeStart] = useState('');
  const [customRangeEnd, setCustomRangeEnd] = useState('');
  // Baseline trend (always "last 6 months") shown only while billingRange is
  // "this_month" — a single MTD figure alone isn't a trend worth charting.
  const [series, setSeries] = useState([]);
  const [rangeSeries, setRangeSeries] = useState([]);
  const [rangeCafeRevenue, setRangeCafeRevenue] = useState({});
  const [rangeTotalRevenue, setRangeTotalRevenue] = useState(0);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState(null);

  // ── Platform fee & gateway margin ────────────────────────────────────────
  // Loads for the default (current) month on mount, and again every time the
  // month picker changes — /internal/gateway-recon calls Razorpay live, so
  // each of those is a real third-party round trip, not a free re-render.
  // "Generate report" stays as a manual re-fetch of whatever month is
  // currently selected (e.g. to retry after an error).
  const [feeMonth, setFeeMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  // Razorpay's half failed (keys missing, API down) but the café fee terms loaded:
  // the table still shows, just without gateway figures.
  const [reconWarning, setReconWarning] = useState(null);

  const generateReport = async () => {
    setReportLoading(true);
    setReportError(null);
    setReconWarning(null);
    try {
      const [year, month] = feeMonth.split('-').map(Number);
      // The fee terms (billing date, rate, console fee) do not depend on Razorpay,
      // so a gateway failure must not hide them.
      const [dueRes, reconResult] = await Promise.all([
        api.get('/internal/platform-fee'),
        api.get('/internal/gateway-recon', { params: { year, month } })
          .catch((err) => ({ failed: err })),
      ]);
      if (reconResult.failed) {
        console.error('Gateway reconciliation unavailable:', reconResult.failed);
        setReconWarning(
          reconResult.failed.response?.data?.detail
          || 'Razorpay figures are unavailable right now.',
        );
      }
      const recon = reconResult.failed ? {} : (reconResult.data || {});
      const byCafe = new Map();
      (dueRes.data || []).forEach((d) => byCafe.set(d.cafe_id, { ...d }));
      (recon.cafes || []).forEach((g) => {
        byCafe.set(g.cafe_id, { ...(byCafe.get(g.cafe_id) || { cafe_id: g.cafe_id, cafe_name: g.cafe_name }), ...g });
      });
      setReport({
        rows: sortTrialsFirst([...byCafe.values()]),
        settledAsAt: recon.settled_as_at,
        totals: recon.platform_totals || {},
        unattributed: recon.unattributed || {},
        month: feeMonth,
      });
    } catch (e) {
      console.error('Failed to build fee report:', e);
      setReportError(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
  // keyed on feeMonth only: generateReport reads feeMonth fresh each render,
  // re-listing it here would just re-run this on every keystroke elsewhere.
  useEffect(() => { generateReport(); }, [feeMonth]);

  // ── Fee start date (per café) ────────────────────────────────────────────
  // ENABLE_PLATFORM_FEE is one switch for the whole fleet, and free trials end
  // on different dates per customer — so each café also carries its own
  // fee_starts_at, and it pays only once BOTH say yes. This is the only place
  // that date can be set: the row lives in the global DB precisely so a café
  // admin cannot reach it.
  const [editingCafe, setEditingCafe] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editNote, setEditNote] = useState('');
  // This café's OWN rate, as a percentage string ("2", "2.5"). Empty = the
  // normal rates. The API takes a fraction, so it is converted at the boundary.
  const [editRate, setEditRate] = useState('');
  // Which box the editor puts the cursor in: the date when opened from the
  // status pill, the rate when opened from the Fee rate pill.
  const [editFocus, setEditFocus] = useState('date');
  const [savingTerms, setSavingTerms] = useState(false);
  const [termsError, setTermsError] = useState(null);

  const openTermsEditor = (row, focus = 'date') => {
    setEditFocus(focus);
    setEditingCafe(row.cafe_id);
    setEditDate(isoToIstDate(row.fee_starts_at));
    setEditNote('');
    setEditRate(row.fee_rate == null ? '' : String(Math.round(row.fee_rate * 10000) / 100));
    setTermsError(null);
  };

  const saveTerms = async (cafeId, { clear = false } = {}) => {
    setTermsError(null);
    const payload = {
      fee_starts_at: clear ? null : dateToIstMidnightIso(editDate),
      note: editNote || null,
    };
    // Clearing the date puts the café back on a free trial; it must not also
    // wipe a negotiated rate, so the rate is only sent on a normal save. A blank
    // box sends null, i.e. "back to the normal rates".
    if (!clear) {
      const pct = editRate.trim();
      if (pct === '') {
        payload.fee_rate = null;
      } else {
        const n = Number(pct);
        if (!Number.isFinite(n) || n < 0 || n > 10) {
          setTermsError('Rate must be a percentage between 0 and 10 (e.g. 2 for 2%).');
          return;
        }
        payload.fee_rate = Math.round(n * 1000) / 100000;
      }
    }
    setSavingTerms(true);
    try {
      const res = await api.put(`/internal/cafes/${cafeId}/fee-terms`, payload);
      // Patch the row in place rather than re-running generateReport: that call
      // hits Razorpay live, and re-billing a third party to redraw one cell is
      // not a trade worth making.
      setReport((prev) => prev && ({
        ...prev,
        rows: prev.rows.map((r) => (r.cafe_id === cafeId
          ? {
            ...r,
            fee_starts_at: res.data.fee_starts_at,
            fee_active: res.data.fee_active,
            fee_rate: res.data.fee_rate,
          }
          : r)),
      }));
      setEditingCafe(null);
    } catch (e) {
      console.error('Failed to save fee terms:', e);
      setTermsError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingTerms(false);
    }
  };

  // Consoles (PS5 / Xbox) are on a testing programme: no platform fee unless a
  // SuperAdmin switches it on for the café. Separate from the PC fee above.
  const [savingConsoleFee, setSavingConsoleFee] = useState(null);

  const toggleConsoleFee = async (row) => {
    const next = !row.console_fee;
    const ok = window.confirm(
      next
        ? `Charge the platform fee on CONSOLE (PS5 / Xbox) sales at ${row.cafe}? PC fees are not affected.`
        : `Stop charging the platform fee on CONSOLE (PS5 / Xbox) sales at ${row.cafe}? PC fees are not affected.`,
    );
    if (!ok) return;
    setSavingConsoleFee(row.cafe_id);
    try {
      const res = await api.put(`/internal/cafes/${row.cafe_id}/console-fee`, { enabled: next });
      setReport((prev) => prev && ({
        ...prev,
        rows: prev.rows.map((r) => (r.cafe_id === row.cafe_id
          ? { ...r, console_fee: res.data.console_fee, console_fee_override: res.data.console_fee_override }
          : r)),
      }));
    } catch (e) {
      console.error('Failed to save console fee:', e);
      window.alert(e.response?.data?.detail || 'Failed to save the console fee switch');
    } finally {
      setSavingConsoleFee(null);
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const head = [
      'cafe_id', 'cafe', 'fee_starts_at', 'fee_active', 'custom_fee_rate_percent',
      'owed_all_time_cash', 'accrued_this_month_cash',
      'retained_gateway', 'razorpay_fee', 'razorpay_gst',
      'margin_excl_gst', 'margin_incl_gst', 'orders', 'data_complete',
    ];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = report.rows.map((r) => [
      r.cafe_id, r.cafe_name, r.fee_starts_at ?? '',
      r.fee_active ? 'yes' : 'no',
      r.fee_rate == null ? '' : Math.round(r.fee_rate * 10000) / 100,
      r.balance_due ?? '', r.accrued_this_month ?? '',
      r.retained ?? '', r.razorpay_fee ?? '', r.razorpay_tax ?? '',
      r.margin_excl_gst ?? '', r.margin_incl_gst ?? '', r.orders ?? '',
      r.reachable === false || r.accruals_reachable === false ? 'NO' : 'yes',
    ].map(esc).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-fee-${report.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [d, c, s] = await Promise.all([
        api.get('/internal/dashboard'),
        api.get('/internal/cafes'),
        api.get('/internal/revenue-series'),
      ]);
      setDash(d.data);
      setCafes(c.data || []);
      setSeries(s.data || []);
    } catch (e) {
      console.error('Failed to load billing:', e);
      setError(e.response?.data?.detail || 'Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Fetches everything the picked range needs at once: the platform-wide
  // monthly series (for the tile total + trend chart) and the per-café
  // breakdown for that SAME window (for the table) — one request pair, kept
  // in sync by construction rather than by two separate range pickers.
  const fetchRangeData = async (range, start, end) => {
    if (range === 'custom' && (!start || !end)) return;
    setRangeLoading(true);
    setRangeError(null);
    try {
      let params;
      if (range === 'custom') {
        // end is EXCLUSIVE server-side, so push it to the next midnight —
        // picking 1st-20th means "through the end of the 20th". Same fix
        // Analytics.jsx's revenue/café trend custom ranges use.
        const endNext = new Date(`${end}T00:00:00`);
        endNext.setDate(endNext.getDate() + 1);
        params = { start, end: endNext.toISOString().slice(0, 10) };
      } else {
        params = { months: REVENUE_RANGE_MONTHS[range] };
      }
      const [seriesRes, trendRes] = await Promise.all([
        api.get('/internal/revenue-series', { params }),
        api.get('/internal/cafe-revenue-trend', { params }),
      ]);
      const s = seriesRes.data || [];
      setRangeSeries(s);
      setRangeTotalRevenue(s.reduce((sum, m) => sum + (m.revenue || 0), 0));
      const byCafe = {};
      (trendRes.data || []).forEach((c) => {
        byCafe[c.cafe_id] = (c.months || []).reduce((sum, m) => sum + (m.revenue || 0), 0);
      });
      setRangeCafeRevenue(byCafe);
    } catch (e) {
      console.error('Failed to load revenue for range:', e);
      setRangeSeries([]);
      setRangeCafeRevenue({});
      setRangeTotalRevenue(0);
      setRangeError(
        e.response?.status === 404
          ? 'This endpoint is not available on the server yet — the backend needs to be updated.'
          : e.response?.data?.detail || 'Could not load revenue data for this range.'
      );
    } finally {
      setRangeLoading(false);
    }
  };

  // Custom range fetches only on explicit "Apply" (below), not per keystroke
  // — see the identical comment on Analytics.jsx's pickers for why.
  useEffect(() => {
    if (billingRange !== 'this_month' && billingRange !== 'custom') {
      fetchRangeData(billingRange, customRangeStart, customRangeEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingRange]);

  const applyCustomBillingRange = () => {
    if (customRangeStart && customRangeEnd) fetchRangeData('custom', customRangeStart, customRangeEnd);
  };

  const isRangeView = billingRange !== 'this_month';
  const activeSeries = isRangeView ? rangeSeries : series;
  // Same year-disambiguation as Analytics.jsx's revenueSpansYears — only
  // matters for a range spanning more than one calendar year.
  const activeSeriesSpansYears = new Set(activeSeries.map((m) => m.year)).size > 1;
  const revenueSeriesData = activeSeries.map((m) => ({
    ...m,
    label: activeSeriesSpansYears ? `${m.month} ${String(m.year).slice(-2)}` : m.month,
  }));

  const revenueTileValue = isRangeView ? rangeTotalRevenue : (dash?.monthly_revenue || 0);
  const revenueTileLabel = billingRange === 'this_month'
    ? 'Revenue (this month)'
    : billingRange === 'custom'
      ? 'Revenue (custom range)'
      : `Revenue (last ${REVENUE_RANGE_MONTHS[billingRange]} months)`;
  const revenueTrendDesc = billingRange === 'this_month'
    ? 'Platform revenue, last 6 months'
    : billingRange === 'custom'
      ? 'Platform revenue, custom range'
      : `Platform revenue, last ${REVENUE_RANGE_MONTHS[billingRange]} months`;

  const byRevenue = [...cafes]
    .map((c) => (isRangeView ? { ...c, revenue: rangeCafeRevenue[c.id] ?? 0 } : c))
    .sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
  const activeLicenses = cafes.filter(
    (c) => c.license_status === 'active' || c.license_status === 'expiring'
  ).length;

  return (
    <div className="page billing-page">
      <style>{`
        .billing-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Billing & Revenue — applied by the same pattern as
             CafeOnboard/Licenses rather than verified against a design.
             The margin-negative red on the fee table (var(--danger, ...))
             and every badge--* below are separate, untouched tokens. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
        .revenue-range-controls { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
        .revenue-range-select { font-size: var(--text-xs); width: 150px; }
        .revenue-range-picker { display: inline-flex; align-items: center; gap: 6px; }
        .revenue-range-picker__input { font-size: var(--text-xs); width: 132px; padding: 6px 8px; }
        .revenue-range-picker__sep { font-size: var(--text-xs); color: var(--text-tertiary); }
        .revenue-range-picker__apply { padding: 6px 12px; font-size: var(--text-xs); }
      `}</style>
      <PageHeader
        title="Billing & Revenue"
        subtitle="Platform revenue and per-café licensing"
        actions={
          <>
            <div className="revenue-range-controls">
              <select value={billingRange} onChange={(e) => setBillingRange(e.target.value)} className="input select revenue-range-select">
                <option value="this_month">This month</option>
                <option value="3m">Last 3 months</option>
                <option value="6m">Last 6 months</option>
                <option value="12m">Last 12 months</option>
                <option value="custom">Custom range</option>
              </select>
              {billingRange === 'custom' && (
                <div className="revenue-range-picker">
                  <input
                    type="date"
                    value={customRangeStart}
                    max={customRangeEnd || todayIso}
                    onChange={(e) => setCustomRangeStart(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyCustomBillingRange()}
                    aria-label="Start date"
                    className="input revenue-range-picker__input"
                  />
                  <span className="revenue-range-picker__sep">–</span>
                  <input
                    type="date"
                    value={customRangeEnd}
                    min={customRangeStart || undefined}
                    max={todayIso}
                    onChange={(e) => setCustomRangeEnd(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyCustomBillingRange()}
                    aria-label="End date"
                    className="input revenue-range-picker__input"
                  />
                  <button
                    type="button"
                    className="btn btn--secondary revenue-range-picker__apply"
                    onClick={applyCustomBillingRange}
                    disabled={!customRangeStart || !customRangeEnd || rangeLoading}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
            <button
              className="btn btn--secondary"
              onClick={() => { fetchAll(); if (isRangeView) fetchRangeData(billingRange, customRangeStart, customRangeEnd); }}
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </>
        }
      />

      {error ? (
        <div className="card">
          <div className="empty-state empty-state--error">
            <p>{error}</p>
            <button className="btn btn--secondary btn--sm" onClick={fetchAll}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <div className="card">
          <div className="empty-state">
            <Loader2 size={28} className="spin" />
            <p>Loading billing…</p>
          </div>
        </div>
      ) : (
        <>
          <div className="stat-grid">
            <StatTile label={revenueTileLabel} value={formatCompact(revenueTileValue)} hint="Top-ups + completed orders, all cafés" />
            <StatTile label="Total cafés" value={dash?.total_cafes ?? 0} hint="Registered tenants" />
            <StatTile label="Active licenses" value={activeLicenses} hint="Active or expiring" />
            <StatTile label="Expiring soon" value={dash?.expiring_soon ?? 0} hint="Within 7 days" />
          </div>

          <Section title="Revenue trend" desc={revenueTrendDesc}>
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              {isRangeView && rangeError ? (
                <div className="empty-state empty-state--error">
                  <AlertTriangle size={20} />
                  <p>{rangeError}</p>
                </div>
              ) : isRangeView && rangeLoading ? (
                <div className="empty-state">
                  <Loader2 size={24} className="spin" />
                </div>
              ) : revenueSeriesData.length === 0 ? (
                <div className="empty-state">
                  <Clock size={20} />
                  <p>No revenue data for this range.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={revenueSeriesData}>
                    <defs>
                      <linearGradient id="billRevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FE7B00" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#FE7B00" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="billRevStroke" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FE7B00" />
                        <stop offset="100%" stopColor="#F7302F" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }} tickFormatter={(v) => formatCompact(v)} />
                    <Tooltip
                      formatter={(v) => [formatCurrency(v), 'Revenue']}
                      contentStyle={{ background: 'rgba(26,26,36,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'white', fontSize: '13px' }}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="url(#billRevStroke)" strokeWidth={2.5} fill="url(#billRevGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Section>

          <Section title="Revenue by café" desc={isRangeView ? 'Selected range, highest first' : 'Month-to-date, highest first'}>
            <div className="card table-card">
              {isRangeView && rangeLoading ? (
                <div className="empty-state">
                  <Loader2 size={24} className="spin" />
                </div>
              ) : (
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Café</th>
                      <th>Owner</th>
                      <th>PCs</th>
                      <th>{isRangeView ? 'Revenue (range)' : 'Revenue (MTD)'}</th>
                      <th>Insights</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byRevenue.length === 0 ? (
                      <tr><td colSpan={5}><div className="empty-state"><p>No cafés found</p></div></td></tr>
                    ) : byRevenue.map((c) => (
                      <tr key={c.id} className="clickable" onClick={() => navigate(`/cafes/${c.id}`)}>
                        <td>{c.name}</td>
                        <td className="text-secondary">{c.owner_name || c.owner_email || '—'}</td>
                        <td>{c.pc_count || 0}</td>
                        <td className="font-mono text-primary">{c.revenue ? formatCurrency(c.revenue) : '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={(e) => { e.stopPropagation(); setInsightsCafe({ id: c.id, name: c.name }); }}
                          >
                            Click here
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </Section>

          <Section
            title="Platform fee & gateway margin"
            desc="What cafés owe you, what Razorpay charged, and what is left"
            actions={
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input
                  type="month"
                  className="input"
                  value={feeMonth}
                  onChange={(e) => setFeeMonth(e.target.value)}
                  style={{ padding: '6px 10px' }}
                />
                <button className="btn btn--primary" onClick={generateReport} disabled={reportLoading}>
                  {reportLoading ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                  {reportLoading ? 'Generating…' : 'Generate report'}
                </button>
                {report && (
                  <button className="btn btn--secondary" onClick={downloadCsv}>
                    <Download size={16} /> CSV
                  </button>
                )}
              </div>
            }
          >
            <div className="card table-card">
              {reportError ? (
                <div className="empty-state empty-state--error"><p>{reportError}</p></div>
              ) : reportLoading && !report ? (
                <div className="empty-state">
                  <Loader2 size={24} className="spin" />
                  <p>Loading {feeMonth}&apos;s report from Razorpay…</p>
                </div>
              ) : !report ? (
                <div className="empty-state">
                  <p>Pick a month to see its report.</p>
                </div>
              ) : (
                <>
                  {reconWarning && (
                    <p className="text-tertiary" style={{ margin: '8px 16px' }}>
                      Gateway figures unavailable ({reconWarning}). Fee terms below are still current.
                    </p>
                  )}
                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Café</th>
                        <th>Billing from</th>
                        <th>Fee rate</th>
                        <th>Console fee</th>
                        <th>Owed to you (cash, all time)</th>
                        <th>You kept (gateway)</th>
                        <th>of which GST</th>
                        <th>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.length === 0 ? (
                        <tr><td colSpan={8}><div className="empty-state"><p>Nothing recorded for this month</p></div></td></tr>
                      ) : report.rows.map((r) => {
                        // A café we could not read reports 0.00, and a zero meaning
                        // "nothing owed" and a zero meaning "we could not look" are the
                        // same number. Never bill on a dash.
                        const blind = r.reachable === false || r.accruals_reachable === false;
                        const margin = r.margin_incl_gst;
                        const startsOn = formatIstDate(r.fee_starts_at);
                        const editing = editingCafe === r.cafe_id;
                        return (
                          <tr key={r.cafe_id} className="clickable" onClick={() => navigate(`/cafes/${r.cafe_id}`)}>
                            <td>
                              {r.cafe_name}
                              {blind && <span className="badge badge--warning" style={{ marginLeft: 8 }}>unreadable</span>}
                            </td>
                            {/* The one editable cell in a table whose rows navigate away, so every
                                control inside it has to stop the click from reaching the row. */}
                            <td onClick={(e) => e.stopPropagation()}>
                              {editing ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                                  <input
                                    type="date"
                                    autoFocus={editFocus === 'date'}
                                    value={editDate}
                                    onChange={(e) => setEditDate(e.target.value)}
                                    disabled={savingTerms}
                                  />
                                  <input
                                    type="number"
                                    autoFocus={editFocus === 'rate'}
                                    min="0"
                                    max="10"
                                    step="0.01"
                                    placeholder="Custom fee rate % (e.g. 2)"
                                    value={editRate}
                                    onChange={(e) => setEditRate(e.target.value)}
                                    disabled={savingTerms}
                                    title="This café's own platform fee, as a percentage. Applies to online AND cash payments. Leave blank for the normal rates."
                                  />
                                  <input
                                    type="text"
                                    placeholder="Note (which deal?)"
                                    value={editNote}
                                    onChange={(e) => setEditNote(e.target.value)}
                                    disabled={savingTerms}
                                  />
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--primary"
                                      disabled={savingTerms || !editDate}
                                      onClick={() => saveTerms(r.cafe_id)}
                                    >
                                      {savingTerms ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn--sm"
                                      disabled={savingTerms}
                                      onClick={() => setEditingCafe(null)}
                                    >
                                      Cancel
                                    </button>
                                    {r.fee_starts_at && (
                                      <button
                                        type="button"
                                        className="btn btn--sm btn--danger"
                                        disabled={savingTerms}
                                        onClick={() => saveTerms(r.cafe_id, { clear: true })}
                                        title="Put this café back on a free trial"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                  {termsError && (
                                    <span className="text-danger" style={{ fontSize: 12 }}>{termsError}</span>
                                  )}
                                  <span className="text-tertiary" style={{ fontSize: 12 }}>
                                    Starts at IST midnight on this date.
                                  </span>
                                  <span className="text-tertiary" style={{ fontSize: 12 }}>
                                    Rate: leave blank for the normal rate
                                    {r.normal_rates ? ` (${normalRatesLabel(r.normal_rates)})` : ''}.
                                    A custom rate replaces it for online and cash.
                                  </span>
                                </div>
                              ) : (() => {
                                const state = feeStateBadge(r, startsOn);
                                return (
                                  <button
                                    type="button"
                                    className={`badge badge--${state.tone}`}
                                    style={{
                                      ...PILL_BUTTON_STYLE,
                                      boxShadow: state.soon ? 'inset 0 0 0 1px var(--status-warning)' : undefined,
                                    }}
                                    onClick={() => openTermsEditor(r)}
                                    title="When this café starts paying the platform fee — click to edit"
                                  >
                                    {state.label}
                                  </button>
                                );
                              })()}
                            </td>
                            {/* Clickable like the status pill, so clicks must not reach the row's navigate. */}
                            <td onClick={(e) => e.stopPropagation()}>
                              {r.fee_rate != null ? (
                                <button
                                  type="button"
                                  className="badge badge--primary"
                                  onClick={() => openTermsEditor(r, 'rate')}
                                  title={`This café's own rate, for online and cash payments. Normal rates: ${normalRatesLabel(r.normal_rates)}. Click to edit.`}
                                  style={{ ...PILL_BUTTON_STYLE, fontWeight: 600 }}
                                >
                                  Custom {fmtRatePct(r.fee_rate)}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="badge badge--neutral"
                                  onClick={() => openTermsEditor(r, 'rate')}
                                  title={`Normal rates: ${normalRatesLabel(r.normal_rates)}. Click to give this café its own rate.`}
                                  style={PILL_BUTTON_STYLE}
                                >
                                  Normal{r.normal_rates ? ` ${normalRatesShort(r.normal_rates)}` : ''}
                                </button>
                              )}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className={`badge badge--${r.console_fee ? 'primary' : 'neutral'}`}
                                disabled={savingConsoleFee === r.cafe_id}
                                onClick={() => toggleConsoleFee(r)}
                                title={`Platform fee on PS5 / Xbox sales — ${r.console_fee ? 'charged' : 'not charged'}${r.console_fee_override == null ? ' (fleet default)' : ''}. Click to ${r.console_fee ? 'turn off' : 'turn on'}. PCs are not affected.`}
                                style={PILL_BUTTON_STYLE}
                              >
                                {r.console_fee ? 'On' : 'Off'}
                              </button>
                            </td>
                            <td className="font-mono">{blind ? '—' : formatFeeCurrency(r.balance_due)}</td>
                            <td className="font-mono">{blind ? '—' : formatFeeCurrency(r.retained)}</td>
                            <td className="font-mono text-tertiary">{formatFeeCurrency(r.razorpay_tax)}</td>
                            <td
                              className="font-mono"
                              style={{ color: margin < 0 ? 'var(--danger, #f87171)' : undefined }}
                            >
                              {margin == null ? '—' : formatFeeCurrency(margin)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <div style={{ padding: 'var(--space-4)', fontSize: 13, lineHeight: 1.7 }} className="text-secondary">
                    <div>
                      Razorpay charged <strong>{formatFeeCurrency(report.totals.razorpay_fee)}</strong> in total
                      {report.totals.razorpay_tax
                        ? <> (including <strong>{formatFeeCurrency(report.totals.razorpay_tax)}</strong> GST)</>
                        : null} across {report.totals.recon_rows ?? 0} settled rows.
                    </div>
                    {report.unattributed?.orders > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <span className="badge badge--warning">unattributed</span>{' '}
                        {formatCurrency(report.unattributed.razorpay_fee)} across {report.unattributed.orders} order(s)
                        could not be matched to a café — a cost that belongs to nobody is still a cost.
                      </div>
                    )}
                    <div style={{ marginTop: 6 }}>
                      <strong>Settled, not captured.</strong> A payment enters Razorpay&apos;s report only once it has
                      settled, so the last days of a month fill in during the next one. Figures as at{' '}
                      {report.settledAsAt ? new Date(report.settledAsAt).toLocaleString() : '—'}.
                    </div>
                    <div style={{ marginTop: 6 }}>
                      Margin shown is after Razorpay&apos;s GST. If that GST is reclaimable as input tax credit, the
                      true margin is higher — the CSV carries both.
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <strong>Billing from</strong> is each café&apos;s own start date, so free trials can end on
                      different days. A future date is fine — it takes effect by itself, nobody has to come back
                      and flip anything. Changing it never bills anything retroactively; it only changes what the
                      café&apos;s <em>next</em> payment costs. The platform-wide <code>ENABLE_PLATFORM_FEE</code>
                      {' '}switch still has to be on, or no date charges anybody.
                    </div>
                  </div>
                </>
              )}
            </div>
          </Section>

        </>
      )}

      {insightsCafe && (
        <CafeInsightsModal
          cafeId={insightsCafe.id}
          cafeName={insightsCafe.name}
          onClose={() => setInsightsCafe(null)}
        />
      )}
    </div>
  );
}
